import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const MentalistPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "What is a mentalist?",
      answer: "A mentalist is a performer who demonstrates highly developed mental abilities such as mind reading, predictions, telepathy, and psychological influence. Unlike traditional magicians who use props and physical illusions, mentalists focus on the power of the mind."
    },
    {
      question: "Who is Simon Nomis?",
      answer: "Simon Nomis is a master mentalist with over 20 years of experience. He has dedicated his career to perfecting the art of mentalism, combining psychology, showmanship, and closely guarded techniques to create experiences that seem genuinely supernatural."
    },
    {
      question: "What can I expect from a mentalist show?",
      answer: "At Mental Hop Scotch, you'll witness Simon reading audience members' thoughts, predicting their choices before they make them, revealing personal information he couldn't possibly know, and demonstrating influence over decisions. The 90-minute show is highly interactive—audience members become the stars."
    },
    {
      question: "Is mentalism the same as being psychic?",
      answer: "Mentalists don't claim supernatural powers. Instead, they use psychology, observation, suggestion, and performance techniques to create the appearance of psychic abilities. The 'how' remains a mystery, which is part of the entertainment."
    },
    {
      question: "Will I be called on stage?",
      answer: "Possibly! Simon works with many audience members throughout the show. While not everyone participates directly, the intimate venue means you'll feel part of the experience regardless. Some participants even walk away with cash prizes and props!"
    },
    {
      question: "Where can I see a mentalist in Orlando?",
      answer: "Simon Nomis performs Mental Hop Scotch every Saturday at 7 PM in Orlando, FL. It's Orlando's premier mentalism experience."
    },
    {
      question: "What's the difference between a mentalist and a magician?",
      answer: "Magicians typically perform illusions with physical objects—cards, rabbits, boxes. Mentalists focus on mental feats—reading minds, making predictions, demonstrating telepathy. Simon Nomis combines both skills but Mental Hop Scotch emphasizes the mentalism and psychological elements."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Mentalist Show Orlando - Mental Hop Scotch",
    "description": "See Orlando's top mentalist Simon Nomis perform incredible feats of mind reading and psychological illusion.",
    "url": "https://www.simonnomislive.com/mentalist",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "performer": { "@type": "Person", "name": "Simon Nomis", "jobTitle": "Master Mentalist" },
    "location": { "@type": "Place", "name": "Orlando, FL", "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL" } },
    "offers": { "@type": "Offer", "price": "35", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": "https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" }
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Mentalist Orlando | Simon Nomis | Mental Hop Scotch Live Show</title>
        <meta name="description" content="Experience Orlando's top mentalist! Simon Nomis performs incredible feats of mind reading and psychological illusion in Mental Hop Scotch." />
        <meta name="keywords" content="mentalist, mentalist Orlando, mentalist show, mental magic, psychological illusion, Simon Nomis, mind reading, mentalism" />
        <link rel="canonical" href="https://www.simonnomislive.com/mentalist" />
        <meta property="og:title" content="Mentalist Orlando | Simon Nomis" />
        <meta property="og:description" content="Experience Orlando's top mentalist! Simon Nomis performs incredible feats of mind reading." />
        <meta property="og:url" content="https://www.simonnomislive.com/mentalist" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6"><span className="text-purple-400">Mentalist</span> in Orlando</h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Master of Psychological Illusion</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Looking for a <strong className="text-purple-400">mentalist</strong> who will blow your mind? Simon Nomis is Orlando's premier mentalist, performing feats of mind reading, prediction, and psychological influence that defy explanation. Experience his acclaimed show, Mental Hop Scotch.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>See the Mentalist</a>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">The <span className="text-purple-400">Mentalist's</span> Abilities</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30">
                <div className="text-4xl mb-4 text-center">🧠</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">Mind Reading</h3>
                <p className="text-gray-300 text-center">Simon accesses your thoughts and reveals them before you speak. Watch as he describes images in your mind and words you're thinking.</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30">
                <div className="text-4xl mb-4 text-center">🔮</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">Prediction</h3>
                <p className="text-gray-300 text-center">Witness predictions made before choices are made—predictions that prove impossible accurate every time.</p>
              </div>
              <div className="bg-purple-900/20 p-6 rounded-lg border border-purple-500/30">
                <div className="text-4xl mb-4 text-center">🎯</div>
                <h3 className="text-xl font-bold text-purple-400 mb-3 text-center">Influence</h3>
                <p className="text-gray-300 text-center">Experience the power of suggestion as Simon demonstrates how subtly he can guide decisions without you realizing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Mentalist Simon Nomis" className="rounded-lg shadow-2xl" />
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">About <span className="text-purple-400">The Mentalist</span></h2>
                <p className="text-gray-300 mb-4">Simon Nomis has spent over 20 years mastering the art of mentalism. His show, Mental Hop Scotch, is a 90-minute journey into the impossible where audience members become the stars.</p>
                <p className="text-gray-300 mb-6">What sets Simon apart is his warmth and charisma. He doesn't just perform tricks—he creates genuine connections with his audience while demonstrating abilities that seem truly supernatural.</p>
                <div className="space-y-2 text-gray-300">
                  <p>🏆 20+ years of experience</p>
                  <p>🎭 Interactive performance style</p>
                  <p>⭐ Award-winning showmanship</p>
                  <p>🎁 Audience members win prizes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Show Details */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">See the <span className="text-purple-400">Mentalist</span> Live</h2>
            <div className="bg-gray-900/50 p-8 rounded-lg border border-purple-500/30">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-purple-400 font-bold mb-2">🎭 Show</h3>
                  <p className="text-white">Mental Hop Scotch</p>
                </div>
                <div>
                  <h3 className="text-purple-400 font-bold mb-2">📅 When</h3>
                  <p className="text-white">Saturdays at 7:00 PM</p>
                </div>
                <div>
                  <h3 className="text-purple-400 font-bold mb-2">📍 Where</h3>
                  <p className="text-white">Orlando, FL</p>
                </div>
                <div>
                  <h3 className="text-purple-400 font-bold mb-2">⏱ Duration</h3>
                  <p className="text-white">90 mins + intermission</p>
                </div>
              </div>
              <div className="text-center">
                <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-lg text-white rounded-full px-8 py-3 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Get Tickets →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Mentalism</span> FAQs</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Experience <span className="text-purple-400">True Mentalism</span></h2>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL | Saturdays at 7PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MentalistPage;